import React, { useState, useEffect } from 'react';
import { useCRM } from '../../context/CRMContext';
import { useEmail } from '../../context/EmailContext';
import SafeIcon from '../../common/SafeIcon';
import { motion } from 'framer-motion';

const DraftComposer = () => {
  const { contacts, deals, companies, projects, permissions } = useCRM();
  const { generateDrafts, attachTrackedLink, markAsSentManual, sendViaAutopilot, loading, settings } = useEmail();

  // Selection State
  const [selectedDealId, setSelectedDealId] = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  
  // Active Draft State
  const [generatedDraft, setGeneratedDraft] = useState(null);
  const [activeVariant, setActiveVariant] = useState('normal');
  const [customBody, setCustomBody] = useState('');
  const [isSending, setIsSending] = useState(false);

  // Derived Data
  const deal = deals.find(d => d.deal_id === selectedDealId);
  
  // Auto-select contact/project if deal is selected
  useEffect(() => {
    if (deal) {
      if (deal.contact_id) setSelectedContactId(deal.contact_id);
    }
  }, [deal]);

  // Update editor when variant changes
  useEffect(() => {
    if (generatedDraft && generatedDraft.variants) {
      const v = generatedDraft.variants[activeVariant];
      setCustomBody(v.body);
    }
  }, [activeVariant, generatedDraft]);

  const handleGenerate = async () => {
    setErrorMsg('');
    const contact = contacts.find(c => c.contact_id === selectedContactId);
    if (!contact) return;

    try {
      const contextData = {
        contact,
        dealId: selectedDealId,
        companyId: contact.company_id,
        projectId: deal?.project_id || '' // Link to project for AI context
      };
      const draft = await generateDrafts(contextData);
      setGeneratedDraft(draft);
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const handleManualSend = () => {
    if (!generatedDraft) return;
    navigator.clipboard.writeText(`${generatedDraft.variants[activeVariant].subject}\n\n${customBody}`);
    markAsSentManual(generatedDraft.draft_id);
    alert('Tekst kopiert til utklippstavle. Lim inn i din e-postklient.');
    setGeneratedDraft(null);
  };

  const handleAutopilotSend = async () => {
    if (!generatedDraft) return;
    setIsSending(true);
    try {
      await sendViaAutopilot(generatedDraft.draft_id);
      alert('E-post lagt i Autopilot-køen!');
      setGeneratedDraft(null);
    } catch (e) {
      alert(`Feil ved autopilot: ${e.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleAddTracking = () => {
    if (generatedDraft) {
      const url = prompt("Hvilken URL vil du spore?");
      if (url) attachTrackedLink(generatedDraft.draft_id, url);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 h-full">
      {/* LEFT: Config */}
      <div className="lg:col-span-4 space-y-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <h3 className="font-black text-slate-900 mb-4 flex items-center gap-2">
            <SafeIcon icon="🎯" className="text-blue-600" /> Kontekst
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Velg Deal (Anbefalt)</label>
              <select 
                value={selectedDealId} 
                onChange={(e) => setSelectedDealId(e.target.value)}
                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none"
              >
                <option value="">-- Start uten deal --</option>
                {deals.map(d => <option key={d.deal_id} value={d.deal_id}>{d.title}</option>)}
              </select>
              <p className="text-[10px] text-slate-400 mt-1">Kobler kontekst fra prosjekt og produkter.</p>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Mottaker</label>
              <select 
                value={selectedContactId} 
                onChange={(e) => setSelectedContactId(e.target.value)}
                className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium outline-none"
              >
                <option value="">-- Velg kontakt --</option>
                {contacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.name} ({c.email})</option>)}
              </select>
            </div>

            {errorMsg && (
              <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-xs font-bold rounded-xl">
                {errorMsg}
              </div>
            )}

            <button 
              onClick={handleGenerate} 
              disabled={loading || !selectedContactId}
              className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {loading ? <SafeIcon icon="🔄" className="animate-spin" /> : <SafeIcon icon="✨" />} 
              AI: Lag 3 forslag
            </button>
          </div>
        </div>

        {generatedDraft && (
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm animate-in fade-in slide-in-from-left-4">
            <h3 className="font-black text-slate-900 mb-4 text-xs uppercase tracking-widest">Velg Strategi</h3>
            <div className="space-y-2">
              {['short', 'normal', 'direct'].map(v => (
                <button
                  key={v}
                  onClick={() => setActiveVariant(v)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${activeVariant === v ? 'border-primary-600 bg-primary-50 text-primary-900' : 'border-slate-100 hover:border-slate-200'}`}
                >
                  <span className="font-bold text-sm capitalize">{v}</span>
                  {activeVariant === v && <SafeIcon icon="✓" />}
                </button>
              ))}
            </div>
            
            <div className="mt-6 pt-6 border-t border-slate-100">
               <button onClick={handleAddTracking} className="w-full py-2 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-lg hover:bg-indigo-100">
                 + Generer Tracked Link
               </button>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: Editor */}
      <div className="lg:col-span-8">
        {generatedDraft ? (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white h-full rounded-[2rem] border border-slate-200 shadow-lg flex flex-col overflow-hidden">
            <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <div>
                <h3 className="font-black text-slate-900 text-sm">Rediger Utkast</h3>
                <p className="text-xs text-slate-500">Emne: {generatedDraft.variants[activeVariant].subject}</p>
              </div>
              <div className="flex gap-2">
                {/* Autopilot Button (Enterprise Only) */}
                {permissions.canEmailAutopilot && settings.autopilot_enabled && (
                  <button 
                    onClick={handleAutopilotSend} 
                    disabled={isSending}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-xs font-bold hover:bg-purple-700 shadow-lg shadow-purple-200 flex items-center gap-2"
                  >
                    {isSending ? <SafeIcon icon="🔄" className="animate-spin" /> : <SafeIcon icon="🚀" />} 
                    Send (Autopilot)
                  </button>
                )}
                
                {/* Manual Button (Always Available) */}
                <button 
                  onClick={handleManualSend} 
                  className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-xs font-bold hover:bg-emerald-600 shadow-lg shadow-emerald-100 flex items-center gap-2"
                >
                  <SafeIcon icon="📋" /> Kopier & Logg
                </button>
              </div>
            </div>
            
            <div className="flex-1 p-6 overflow-y-auto bg-slate-50/30">
              <textarea 
                value={customBody} 
                onChange={(e) => setCustomBody(e.target.value)}
                className="w-full h-full bg-transparent border-none outline-none resize-none text-slate-700 text-sm leading-relaxed whitespace-pre-wrap"
              />
            </div>
            
            <div className="p-3 bg-yellow-50 text-yellow-800 text-[10px] font-bold text-center border-t border-yellow-100">
              {settings.autopilot_enabled ? 
                "Autopilot er aktiv. Du kan sende direkte." : 
                "Copilot Modus: Du må sende e-posten selv via din e-postklient."}
            </div>
          </motion.div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200 text-slate-400">
            <SafeIcon icon="🤖" className="w-16 h-16 mb-4 opacity-30" />
            <p className="font-bold text-sm">Klar til å skrive?</p>
            <p className="text-xs mt-1">Velg en deal eller kontakt til venstre.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DraftComposer;